import { motion } from 'framer-motion';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';

import Header from '@/common/header';
import { FaqAccordion } from '@/components/trial/shared/faq-accordion';
import { PingCounter } from '@/components/trial/shared/ping-counter';
import { bai_jamjuree } from '@/public/assets/fonts/Bai-Jamjuree';
import { outfit } from '@/public/assets/fonts/Outfit';

import CreateAccountV2 from '../../create-account-v2';
import { Footer } from '../../footer';
import { NoPingAppVideo } from '../../shared/noping-app-video';
import type { DefaultTrialProps } from '../../trial-layout-switcher';
import { CircuitBg, DangerDotsBg, ShieldGlowBg, TechGridBg } from './components/backgrounds';
import { BG_DARK, CARD_BG, DANGER_RED, EASE_OUT, HERO_BENEFITS, ISP_PIPES, MUTED_GRAY, NOPING_GREEN, NOPING_STATS, PRO_ENDORSEMENTS, PROBLEM_STATS, SHIELD_BLUE, SHIELD_CYAN, SHIELD_FEATURES, SWORD_FEATURES, TECH_STATS, V5A_TOOLS } from './components/constants';
import { ConnectionPulse, GameCarousel, ISPPipe, ShieldDiagram, SwordDiagram, ToolIcon } from './components/interactive';
import { FadeSection, GlowCard, Section, SectionTitle } from './components/ui-helpers';

interface DigitalFortressProps extends DefaultTrialProps {}

export function DigitalFortress({ daysTrial, trialConfigId, trialPageId, isDisabledButton = false }: DigitalFortressProps) {
  const { t } = useTranslation(['components/trial/lp/v5a']);
  const [failedPipe, setFailedPipe] = useState<number | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setFailedPipe(0);
      setTimeout(() => setFailedPipe(null), 2500);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const faqItems = Array.from({ length: 6 }, (_, i) => ({ q: t(`faq.items.${i}.q`), a: t(`faq.items.${i}.a`) }));

  return (
    <div className={`${bai_jamjuree.className} ${outfit.variable} relative min-h-screen`} style={{ background: BG_DARK, color: '#E5E7EB' }}>
      <Header variant='primary' />

      {/* ══ SECTION 1 — HERO ══════════════════════════════════ */}

      <section className='relative flex min-h-screen items-center overflow-hidden pb-8 pt-20' style={{ zIndex: 1 }}>
        <TechGridBg />

        <div className='relative z-10 mx-auto max-w-7xl px-4 md:px-8'>
          <div className='grid items-center gap-8 lg:grid-cols-2 lg:gap-12'>
            {/* LEFT — Content */}
            <div className='pt-4'>
              <FadeSection>
                <motion.div className='mb-4 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-widest' style={{ backgroundColor: `${SHIELD_CYAN}10`, borderColor: `${SHIELD_CYAN}30`, color: SHIELD_CYAN, fontFamily: 'var(--font-bai)' }} animate={{ boxShadow: [`0 0 20px ${SHIELD_CYAN}00`, `0 0 20px ${SHIELD_CYAN}30`, `0 0 20px ${SHIELD_CYAN}00`] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' as const }}>
                  <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke={SHIELD_CYAN} strokeWidth='2.5'>
                    <path d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' />
                  </svg>
                  {t('hero.badge')}
                </motion.div>
              </FadeSection>

              <FadeSection delay={0.1}>
                <h1 className='mb-4 text-4xl font-bold leading-[1.1] md:text-5xl lg:text-6xl' style={{ fontFamily: 'var(--font-bai)', color: '#FFFFFF' }}>
                  {t('hero.title_prefix')} <span style={{ color: SHIELD_CYAN }}>{t('hero.title_accent')}</span>
                </h1>
              </FadeSection>

              <FadeSection delay={0.2}>
                <p className='mb-5 max-w-xl text-lg leading-relaxed md:text-xl' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                  {t('hero.description')}
                </p>
              </FadeSection>

              <FadeSection delay={0.3}>
                <div className='mb-5'>
                  <PingCounter startPing={187} endPing={5} duration={2.5} accentColor={SHIELD_CYAN} size='lg' labelOptimizing={t('ping.optimizing')} labelDone={t('ping.done')} />
                </div>
              </FadeSection>

              <FadeSection delay={0.4}>
                <div className='overflow-hidden rounded-xl border shadow-2xl' style={{ borderColor: `${SHIELD_CYAN}20`, boxShadow: `0 20px 60px ${SHIELD_CYAN}10` }}>
                  <NoPingAppVideo />
                </div>
              </FadeSection>
            </div>

            {/* RIGHT — CTA card */}
            <div className='relative lg:sticky lg:top-24'>
              <GlowCard glowColor={SHIELD_CYAN}>
                <div className='mb-4'>
                  <h3 className='mb-1 text-xl font-bold md:text-2xl' style={{ fontFamily: 'var(--font-bai)', color: '#FFFFFF' }}>
                    {t('hero.form_title')}
                  </h3>
                  <p className='text-sm' style={{ color: SHIELD_CYAN, fontFamily: 'var(--font-outfit)' }}>
                    {t('hero.form_subtitle', { days: daysTrial ?? 1 })}
                  </p>
                </div>
                <CreateAccountV2 daysTrial={daysTrial} customTrialData={{ custom: trialConfigId, trialPageId }} isDisabledButton={isDisabledButton} formId='v5a-hero-form' />
                <p className='mt-3 text-center text-xs' style={{ color: MUTED_GRAY }}>
                  {t('hero.form_offer')}
                </p>
              </GlowCard>

              <div className='mt-4 flex flex-wrap justify-center gap-2'>
                {HERO_BENEFITS.map((_, i) => (
                  <span key={i} className='rounded-full border px-3 py-1.5 text-xs font-medium' style={{ backgroundColor: `${SHIELD_CYAN}08`, borderColor: `${SHIELD_CYAN}25`, color: SHIELD_CYAN, fontFamily: 'var(--font-outfit)' }}>
                    {t(`hero.benefits.${i}`)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ SECTION 2 — THE PROBLEM ═══════════════════════════ */}

      <Section bg={<DangerDotsBg />}>
        <SectionTitle subtitle={t('problem.subtitle')} title={t('problem.title')} description={t('problem.description')} accentColor={DANGER_RED} />

        <FadeSection>
          <div className='mb-16 grid gap-6 md:grid-cols-2'>
            {PROBLEM_STATS.map((stat, i) => (
              <div key={i} className='rounded-xl border px-6 py-8 text-center' style={{ backgroundColor: `${DANGER_RED}08`, borderColor: `${DANGER_RED}25` }}>
                <div className='mb-2 text-5xl font-black md:text-6xl' style={{ fontFamily: 'var(--font-bai)', color: DANGER_RED }}>
                  {stat.value}
                </div>
                <p className='text-sm' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                  {t(`problem.stats.${i}`)}
                </p>
              </div>
            ))}
          </div>
        </FadeSection>

        <FadeSection delay={0.2}>
          <div className='grid gap-8 md:grid-cols-2'>
            <div className='rounded-xl border p-6' style={{ backgroundColor: CARD_BG, borderColor: `${DANGER_RED}25` }}>
              <div className='mb-4 flex items-center gap-3'>
                <div className='size-3 rounded-full' style={{ backgroundColor: DANGER_RED }} />
                <h3 className='text-lg font-bold' style={{ fontFamily: 'var(--font-bai)', color: DANGER_RED }}>
                  {t('problem.without_title')}
                </h3>
              </div>
              <p className='mb-4 text-xs' style={{ color: MUTED_GRAY }}>
                {t('problem.without_desc')}
              </p>
              <ConnectionPulse stable={false} color={DANGER_RED} />
              <div className='mt-4 flex justify-between text-xs' style={{ color: DANGER_RED }}>
                <span>{t('problem.without_loss')}</span>
                <span>{t('problem.without_disconnects')}</span>
              </div>
            </div>

            <div className='rounded-xl border p-6' style={{ backgroundColor: CARD_BG, borderColor: `${NOPING_GREEN}25` }}>
              <div className='mb-4 flex items-center gap-3'>
                <div className='size-3 rounded-full' style={{ backgroundColor: NOPING_GREEN, boxShadow: `0 0 8px ${NOPING_GREEN}60` }} />
                <h3 className='text-lg font-bold' style={{ fontFamily: 'var(--font-bai)', color: NOPING_GREEN }}>
                  {t('problem.with_title')}
                </h3>
              </div>
              <p className='mb-4 text-xs' style={{ color: MUTED_GRAY }}>
                {t('problem.with_desc')}
              </p>
              <ConnectionPulse stable color={NOPING_GREEN} />
              <div className='mt-4 flex justify-between text-xs' style={{ color: NOPING_GREEN }}>
                <span>{t('problem.with_loss')}</span>
                <span>{t('problem.with_disconnects')}</span>
              </div>
            </div>
          </div>
        </FadeSection>
      </Section>

      {/* ══ SECTION 3 — HOW IT WORKS ══════════════════════════ */}

      <Section bg={<CircuitBg />} className='bg-gradient-to-b from-[#080809] via-[#0A0E14] to-[#080809]'>
        <SectionTitle subtitle={t('how.subtitle')} title={t('how.title')} description={t('how.description')} accentColor={SHIELD_CYAN} />

        <FadeSection>
          <div className='grid items-stretch gap-12 lg:grid-cols-2'>
            <div className='flex flex-col justify-between rounded-xl border p-6 md:p-8' style={{ backgroundColor: `${CARD_BG}CC`, borderColor: `${SHIELD_CYAN}20` }}>
              <h4 className='mb-6 text-sm font-semibold uppercase tracking-widest' style={{ color: SHIELD_CYAN, fontFamily: 'var(--font-bai)' }}>
                {t('how.monitor_title')}
              </h4>

              {ISP_PIPES.map((pipe, i) => (
                <ISPPipe key={pipe.name} name={pipe.name} latency={pipe.latency} status={pipe.status} color={pipe.color} failed={failedPipe === i} delay={i * 0.1} />
              ))}

              <div className='mt-6 border-t pt-4' style={{ borderColor: `${SHIELD_CYAN}20` }}>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke={SHIELD_CYAN} strokeWidth='2.5'>
                      <path d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' />
                    </svg>
                    <span className='text-sm font-semibold' style={{ color: SHIELD_CYAN }}>
                      {t('how.merged_output')}
                    </span>
                  </div>
                  <span className='font-mono text-sm' style={{ color: NOPING_GREEN }}>
                    {failedPipe !== null ? t('how.active_5') : t('how.active_6')}
                  </span>
                </div>
                <div className='mt-2 h-2 overflow-hidden rounded-full' style={{ backgroundColor: `${SHIELD_CYAN}15` }}>
                  <motion.div className='h-full rounded-full' style={{ background: `linear-gradient(90deg, ${SHIELD_CYAN}, ${NOPING_GREEN})` }} animate={{ width: failedPipe !== null ? '83%' : '100%' }} transition={{ duration: 0.5, ease: EASE_OUT }} />
                </div>
              </div>
            </div>

            <div className='space-y-6'>
              {TECH_STATS.map((item, i) => (
                <GlowCard key={i} glowColor={SHIELD_CYAN} delay={i * 0.1}>
                  <div className='flex items-start gap-4'>
                    <div className='min-w-[80px] shrink-0 font-mono text-2xl font-black md:text-3xl' style={{ color: SHIELD_CYAN, fontFamily: 'var(--font-bai)' }}>
                      {item.stat}
                    </div>
                    <div>
                      <h4 className='mb-1 text-sm font-bold text-white' style={{ fontFamily: 'var(--font-bai)' }}>
                        {t(`how.stats.${i}.label`)}
                      </h4>
                      <p className='text-xs leading-relaxed' style={{ color: MUTED_GRAY }}>
                        {t(`how.stats.${i}.desc`)}
                      </p>
                    </div>
                  </div>
                </GlowCard>
              ))}
            </div>
          </div>
        </FadeSection>
      </Section>

      {/* ══ SECTION 4 — SHIELD + SWORD ════════════════════════ */}

      <Section>
        <SectionTitle subtitle={t('defense.subtitle')} title={t('defense.title')} description={t('defense.description')} accentColor={NOPING_GREEN} />

        <FadeSection>
          <div className='grid gap-8 md:grid-cols-2'>
            <motion.div className='group relative overflow-hidden rounded-xl border' style={{ backgroundColor: CARD_BG, borderColor: `${SHIELD_CYAN}30` }} initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} whileHover={{ borderColor: `${SHIELD_CYAN}60` }}>
              <ShieldDiagram failedPipe={failedPipe} />
              <div className='relative z-10 p-8 pt-4'>
                <div className='mb-2 text-xs font-bold uppercase tracking-widest' style={{ color: SHIELD_CYAN, fontFamily: 'var(--font-bai)' }}>
                  {t('defense.shield_label')}
                </div>
                <h3 className='mb-4 text-2xl font-bold text-white' style={{ fontFamily: 'var(--font-bai)' }}>
                  {t('defense.shield_name')}
                </h3>
                <ul className='space-y-3'>
                  {SHIELD_FEATURES.map((_, i) => (
                    <motion.li key={i} className='flex items-start gap-3 text-sm' style={{ color: '#CCC' }} initial={{ opacity: 0, x: -15 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 + i * 0.08 }}>
                      <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke={SHIELD_CYAN} strokeWidth='3' className='mt-0.5 shrink-0'>
                        <path d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' />
                      </svg>
                      {t(`defense.shield_features.${i}`)}
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>

            <motion.div className='group relative overflow-hidden rounded-xl border' style={{ backgroundColor: CARD_BG, borderColor: `${NOPING_GREEN}30` }} initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }} whileHover={{ borderColor: `${NOPING_GREEN}60` }}>
              <SwordDiagram />
              <div className='relative z-10 p-8 pt-4'>
                <div className='mb-2 text-xs font-bold uppercase tracking-widest' style={{ color: NOPING_GREEN, fontFamily: 'var(--font-bai)' }}>
                  {t('defense.sword_label')}
                </div>
                <h3 className='mb-4 text-2xl font-bold text-white' style={{ fontFamily: 'var(--font-bai)' }}>
                  {t('defense.sword_name')}
                </h3>
                <ul className='space-y-3'>
                  {SWORD_FEATURES.map((_, i) => (
                    <motion.li key={i} className='flex items-start gap-3 text-sm' style={{ color: '#CCC' }} initial={{ opacity: 0, x: -15 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 + i * 0.08 }}>
                      <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke={NOPING_GREEN} strokeWidth='3' className='mt-0.5 shrink-0'>
                        <polygon points='13 2 3 14 12 14 11 22 21 10 12 10 13 2' />
                      </svg>
                      {t(`defense.sword_features.${i}`)}
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </FadeSection>

        <FadeSection delay={0.3}>
          <div className='mt-12 rounded-xl border px-6 py-10 text-center' style={{ background: `linear-gradient(135deg, ${SHIELD_CYAN}08, ${NOPING_GREEN}08)`, borderColor: `${SHIELD_CYAN}20` }}>
            <p className='text-lg font-semibold md:text-xl' style={{ fontFamily: 'var(--font-bai)', color: '#FFFFFF' }}>
              {t('defense.combined_statement')} <span style={{ color: NOPING_GREEN }}>{t('defense.combined_accent')}</span>
            </p>
            <p className='mt-2 text-sm' style={{ color: MUTED_GRAY }}>
              {t('defense.patent')}
            </p>
          </div>
        </FadeSection>
      </Section>

      {/* ══ SECTION 5 — THE FULL ARSENAL ══════════════════════ */}

      <Section className='bg-gradient-to-b from-[#080809] via-[#0C0D12] to-[#080809]'>
        <SectionTitle subtitle={t('arsenal.subtitle')} title={t('arsenal.title')} description={t('arsenal.description')} accentColor={NOPING_GREEN} />

        <div className='grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
          {V5A_TOOLS.map((tool, i) => (
            <GlowCard key={tool.name} glowColor={tool.accent} delay={i * 0.08} className={tool.star ? 'md:col-span-2 lg:col-span-1' : ''}>
              <div className={tool.star ? 'relative' : ''}>
                {tool.star && (
                  <div className='absolute -end-3 -top-3 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider' style={{ backgroundColor: SHIELD_CYAN, color: BG_DARK, fontFamily: 'var(--font-bai)' }}>
                    {t('arsenal.featured')}
                  </div>
                )}

                <div className='mb-4 flex size-12 items-center justify-center rounded-xl' style={{ backgroundColor: `${tool.accent}15` }}>
                  <ToolIcon icon={tool.icon} color={tool.accent} />
                </div>

                <h4 className='mb-2 text-lg font-bold' style={{ fontFamily: 'var(--font-bai)', color: tool.star ? SHIELD_CYAN : '#FFFFFF' }}>
                  {t(`arsenal.tools.${i}.name`)}
                </h4>
                <p className='text-sm leading-relaxed' style={{ color: MUTED_GRAY }}>
                  {t(`arsenal.tools.${i}.desc`)}
                </p>
              </div>
            </GlowCard>
          ))}
        </div>
      </Section>

      {/* ══ SECTION 6 — PRO PLAYERS ═══════════════════════════ */}

      <Section>
        <SectionTitle subtitle={t('pros.subtitle')} title={t('pros.title')} description={t('pros.description')} accentColor={NOPING_GREEN} />

        <div className='grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
          {PRO_ENDORSEMENTS.map((endorsement, i) => (
            <GlowCard key={endorsement.name} glowColor={NOPING_GREEN} delay={i * 0.08}>
              <div className='mb-4 flex items-center gap-3'>
                <div className='size-12 shrink-0 overflow-hidden rounded-full border-2' style={{ borderColor: `${NOPING_GREEN}40` }}>
                  <Image src={endorsement.image} alt={endorsement.name} width={48} height={48} className='size-full object-cover' />
                </div>
                <div>
                  <h4 className='text-sm font-bold text-white' style={{ fontFamily: 'var(--font-bai)' }}>
                    {endorsement.name}
                  </h4>
                  <p className='text-xs' style={{ color: NOPING_GREEN }}>
                    {t(`pros.items.${i}.title`)}
                  </p>
                  <p className='text-xs' style={{ color: MUTED_GRAY }}>
                    {t(`pros.items.${i}.game`)}
                  </p>
                </div>
              </div>
              <p className='text-sm italic leading-relaxed' style={{ color: '#CCC', fontFamily: 'var(--font-outfit)' }}>
                &ldquo;{t(`pros.items.${i}.quote`)}&rdquo;
              </p>
            </GlowCard>
          ))}
        </div>
      </Section>

      {/* ══ SECTION 7 — GAME LIBRARY ══════════════════════════ */}

      <Section className='bg-gradient-to-b from-[#080809] via-[#0A0E14] to-[#080809]'>
        <SectionTitle subtitle={t('games.subtitle')} title={t('games.title').replace('{{count}}', NOPING_STATS.gamesSupported)} description={t('games.description')} accentColor={SHIELD_BLUE} />

        <FadeSection>
          <GameCarousel />
        </FadeSection>
      </Section>

      {/* ══ SECTION 8 — FAQ ═══════════════════════════════════ */}

      <Section>
        <SectionTitle subtitle={t('faq.subtitle')} title={t('faq.title')} description={t('faq.description')} accentColor={SHIELD_CYAN} />

        <FadeSection>
          <div className='mx-auto max-w-3xl'>
            <FaqAccordion items={faqItems} accentColor={SHIELD_CYAN} />
          </div>
        </FadeSection>
      </Section>

      {/* ══ SECTION 9 — FINAL CTA ═════════════════════════════ */}

      <section className='relative overflow-hidden px-4 py-20 md:px-8 md:py-28'>
        <ShieldGlowBg />

        <motion.div className='absolute left-1/2 top-1/2 size-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-5' style={{ background: `radial-gradient(circle, ${SHIELD_CYAN}, transparent 60%)` }} animate={{ scale: [1, 1.2, 1], opacity: [0.05, 0.1, 0.05] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' as const }} aria-hidden='true' />

        <div className='relative z-10 mx-auto max-w-2xl'>
          <FadeSection>
            <div className='mb-10 text-center'>
              <div className='mb-6 flex justify-center'>
                <motion.div className='flex size-20 items-center justify-center rounded-2xl' style={{ backgroundColor: `${SHIELD_CYAN}15`, border: `2px solid ${SHIELD_CYAN}30` }} animate={{ boxShadow: [`0 0 30px ${SHIELD_CYAN}00`, `0 0 30px ${SHIELD_CYAN}40`, `0 0 30px ${SHIELD_CYAN}00`] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' as const }}>
                  <svg width='40' height='40' viewBox='0 0 24 24' fill='none' stroke={SHIELD_CYAN} strokeWidth='2'>
                    <path d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' />
                  </svg>
                </motion.div>
              </div>

              <h2 className='mb-4 text-3xl font-bold md:text-4xl lg:text-5xl' style={{ fontFamily: 'var(--font-bai)', color: '#FFFFFF' }}>
                {t('cta.headline')} <span style={{ color: SHIELD_CYAN }}>{t('cta.headline_accent')}</span>
              </h2>
              <p className='text-lg' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                {t('cta.description', { days: daysTrial ?? 1 })}
              </p>
            </div>
          </FadeSection>

          <FadeSection delay={0.2}>
            <GlowCard glowColor={SHIELD_CYAN}>
              <div className='mb-4'>
                <h3 className='mb-1 text-xl font-bold md:text-2xl' style={{ fontFamily: 'var(--font-bai)', color: '#FFFFFF' }}>
                  {t('cta.form_title')}
                </h3>
                <p className='text-sm' style={{ color: SHIELD_CYAN, fontFamily: 'var(--font-outfit)' }}>
                  {t('cta.form_subtitle')}
                </p>
              </div>
              <CreateAccountV2 daysTrial={daysTrial} customTrialData={{ custom: trialConfigId, trialPageId }} isDisabledButton={isDisabledButton} formId='v5a-final-form' />
            </GlowCard>
          </FadeSection>

          <FadeSection delay={0.4}>
            <div className='mt-8 text-center'>
              <p className='text-xs' style={{ color: MUTED_GRAY }}>
                {t('cta.trust', { users: NOPING_STATS.users, servers: NOPING_STATS.servers, countries: NOPING_STATS.countries })}
              </p>
            </div>
          </FadeSection>
        </div>
      </section>

      <Footer text={t('footer', { year: new Date().getFullYear() })} />
    </div>
  );
}
